import React, { useRef, useLayoutEffect, useState } from "react";
import { Helmet } from "react-helmet";

import { Logo } from "./src/App";

const uoftImage = './images/uoft-logo-blue.svg';
const ecosysImage = "./images/ecosys.png";
const vectorImage = "./images/vector_logo.png";
const hotlineDemoImage = "./images/hotlineDemo.jpg";
const hotlinePaperImage = "./images/hotlinePaperScreenshot.jpg";
const githubPageImage = "./images/github-page.png";
const hotlineImage = "./images/hotlineUI.png";
const tensorboardImage = "./images/tensorboardUI.png";



function Header(props) {
  return (
    <div className="text-center">
      <div className="pt-11 text-3xl">
        <div className="text-center">
          <div className="pb-10 w-80 mx-auto">
            <Logo containerClass='p-1 h-[40px] relative'/>
          </div>
          Hotline Profiler: Automatic Annotation and A Multi-Scale Timeline for Visualizing Time-Use in DNN Training
        </div>
      </div>
      <div className="pt-6">
        <span><a className='text-black' href='/'><u>Daniel Snider</u></a>, Fanny Chevalier, Gennady Pekhimenko</span>
      </div>
      <div className="pt-1">
        University of Toronto
      </div>
      <div className="pt-5 italic">
        In Proceedings of Machine Learning and Systems (MLSys 2023)
      </div>
    </div>
  )
}

function Link(props) {
  return (
    <a href={props.url} target="_blank" className="text-center hover:opacity-50 ">
      <img className="h-[100px] border" src={props.img}/>
      <div className="">
        {props.icon}
        {props.name}
      </div>
    </a>
  )
}

function Links(props) {
  return (
    <div className="pt-9">
      <div className="flex justify-center gap-11">
        <Link
          name="Paper"
          url="./papers/Hotline.pdf"
          img={hotlinePaperImage}
          icon={<i class="pr-1 bi bi-file-earmark-text"></i>}
        />
        <Link
          name="Demo"
          url="./hotline/demo"
          img={hotlineDemoImage}
          icon={<i class="pr-1 bi bi-window"></i>}
        />
        <Link
          name="GitHub"
          url="https://github.com/UofT-EcoSystem/hotline"
          img={githubPageImage}
          icon={<i class="pr-1 bi bi-code-square"></i>}
        />
      </div>
    </div>
  );
}

function Abstract(props) {
  return (
    <div className="pt-8">
      <div class="pb-6 border-t border-gray-300 w-full  opacity-50"></div> {/* horizontal line */}
      <div className="about-title text-bold text-2xl pb-1">Abstract</div>
      <div className="text-justify">
        To help ML developers investigate and understand time-use in DNN training, we propose Hotline, a novel profiler designed specifically for runtime bottleneck identification. Hotline is the first profiler to automatically annotate a standard data format for program runtime traces with DNN concepts that most ML developers are familiar with, i.e. the DNN training loop and model architecture. Hotline does so without modifying DNN libraries or making use of vendor-specific tools and introduces no additional overhead on measurements. We further introduce noise reduction techniques and a multi-scale timeline visualization to make the presentation of DNN runtime data more insightful, familiar, and easy to navigate. We demonstrate Hotline’s utility through in-depth case studies of finding bottlenecks in real-world DNN applications and we report on a user study with 17 software developers in which most participants were able to perform common performance investigation tasks in under 30 seconds (avg = 26 sec) and further commented that Hotline’s visualization “Takes less time to find insights compared to existing approaches”.
      </div>
    </div>
  )
}

function Figure(props) {
  return (
    <div className="text-center">
      <div className="py-1">
        <div className="flex justify-center gap-1">
          <div className="font-bold">
            {props.leftText}
          </div>
          <div>
            {props.rightText}
          </div>
        </div>
      </div>
      <img className="max-h-[300px] w-full border" src={props.img}/>
    </div>
  )
}


function Figures(props) {
  return (
    <div className="pt-10">
      <div className="flex justify-center gap-6">
        <Figure
          leftText="Existing:"
          rightText="Tensorboard"
          img={tensorboardImage}
        />
        <Figure
          leftText="Ours:"
          rightText="Hotline"
          img={hotlineImage}
        />
      </div>
    </div>
  )
}

function Motivation(props) {
  return (
    <div className="pt-6">
      <div className="about-title text-bold text-2xl pb-1">Motivation</div>
      {/* Most ML developers do not profile runtime, even though they should. Where did all the time/money go? */}
      <div className="text-justify">
        Profiling is a standard practice used to investigate the efficiency of software and hardware operation at runtime and is a crucial part of proving new concepts, debugging problems, and optimizing performance. However, most machine learning (ML) developers find profiling secondary to their goal of improving model accuracy or just too difficult (especially with existing ML tools).
      </div>
      <div className="pt-4 flex gap-5">
        <div className="">
          <div className="font-bold">Why is profiling important?</div>
          <ul class="pl-1 mt-1 list-inside">
            <li>Why 1: Find Bottlenecks</li>
            <li>Why 2: Communicate Findings</li>
            <li>Why 3: Guide Optimization</li>
          </ul>
        </div>
        <div className="">
          <div className="font-bold">Why is profiling hard?</div>
          <ul class="pl-1 mt-1 list-inside">
            <li>Challenge 1: Information Overload</li>
            <li>Challenge 2: Disparate Granularities</li>
            <li>Challenge 3: Discontiguous Execution</li>
          </ul>
        </div>
        <div className="">
          <div className="font-bold">What can be done to improve?</div>
          <ul class="pl-1 mt-1 list-inside">
            <li>Goal 1: Easy to Navigate</li>
            <li>Goal 2: Reduce Noise</li>
            <li>Goal 3: More Familiar</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

function Contributions(props) {
  return (
    <div className="pt-6">
      <div className="about-title text-bold text-2xl pb-1">Contributions</div>
      <div className="flex gap-5">
        <div className="">
          <div className="font-bold">1) Automatic Annotation of DNN Concepts</div>
          <div className="pt-1">
            Reconciling operations defined in DNN models to operations in DNN runtime traces is non-trivial and tedious to do manually so we have developed an automatic algorithm. Hotline generates three types of annotations that existing profilers do not offer: stages of training, model architecture, and arbitrary but interesting sections not defined in the user’s source code, such as inter-GPU communication.
          </div>
        </div>
        <div className="">
          <div className="font-bold">2) Novel Multi-Scale Timeline</div>
          <div className="pt-1">
            We introduce the Multi-Scale Timeline and show it is a promising visualization technique that is well suited for navigating and interpreting the complex timelines found in DNN training and which may have broad utility in the field of systems profiling.
          </div>
        </div>
        <div className="">
          <div className="font-bold">3) Evaluation of Utility</div>
            <div className="pt-1">
              We present in-depth case studies illustrating Hotline’s unique advantages when used to investigate time-use bottlenecks. Additionally, we report on software developers’ qualitative impressions of Hotline’s design and show how rapidly common performance investigation tasks can be performed.
            </div>
        </div>
      </div>
    </div>
  )
}

function Citation(props) {
  const cite = `@article{2023Hotline,
  title={Hotline Profiler: Automatic Annotation and A Multi-Scale Timeline for Visualizing Time-Use in DNN Training},
  author={Snider, Daniel and Chevalier, Fanny and Pekhimenko, Gennady},
  journal={Conference on Machine Learning and Systems (MLSys)},
  year={2023}
}`

  return (
    <div className="pt-6">
      <div className="about-title text-bold text-2xl pb-1">Citation</div>
      <div className="pt-4">
        <div className="pub-bibtex">
          <pre className="pub-cite-text">{cite}</pre>
          <button
            className="pub-bibtex-copy"
            onClick={() => {navigator.clipboard.writeText(cite)}}
          > Copy </button>
        </div>
      </div>
    </div>
  )
}

function Acknowledgements(props) {
  return (
    <div className="pb-8">
      <span className="font-bold">
        Acknowledgements:&nbsp;
      </span>
      <span className="">
      This project was supported by the Canada Foundation for Innovation JELF grant, NSERC Discovery grant, AWS Machine Learning Research Award (MLRA), Facebook Faculty Research Award, Google Scholar Research Award, and VMware Early Career Faculty Grant.
      </span>
    </div>
  )
}

function Logos(props) {
  return (
    <div className="">
      <div className="pb-6 border-t border-gray-300 w-full  opacity-50"></div> {/* horizontal line */}
      <div className="flex gap-20 justify-center">
        <img height='60' src={ecosysImage}></img>
        <img height='60' src={uoftImage}></img>
        <img height='50' className="pt-[12px]" src={vectorImage}></img>
      </div>
    </div>
  )
}

export function HotlineAbout(props){
  return (
    <div className='main'>
      <Helmet>
        <title>Hotline About</title>
        <link rel="icon" href="data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22><text y=%22.9em%22 font-size=%2290%22>🔥</text></svg>"></link>
      </Helmet>
      <div className="header">
        <div className="grey-color-bar"></div>
        <div className="px-4 secondary-page">
          <Header />
          <Links />
          <Abstract />
          <Figures />
          <Motivation />
          <Contributions />
          <Citation />
          <Acknowledgements />
          <Logos />
        </div>
      </div>
      <div className="grey-color-bar-bottom"></div>
    </div>
  )
}

